import React from 'react';
import { createFileRoute, Outlet, useLocation } from '@tanstack/react-router';
import { endOfDay, format, startOfDay } from 'date-fns';
import { SegmentedTabs } from '@/components/segmented-tabs';
import { fetchCampaign } from '@/services/campaign';
import { DATE_FORMAT_ISO } from '@/constants/date';
import { zodValidator } from '@tanstack/zod-adapter';
import { useTranslation } from 'react-i18next';
import i18next from '@/i18next';
import { dateRangeSchema } from '@/schema/date-range-schema';
import { z } from 'zod';
import { FiltersRow } from '@/components/filter-row';
import { AnalyticsFilter } from '@/components/analytics-filter';
import { Badge } from '@/components/ui/badge';
const abSearchSchema = dateRangeSchema.extend({
    excludeDevices: z.array(z.string()).default(['bot']),
    excludeBrowsers: z.array(z.string()).default(['bot']),
});
export const Route = createFileRoute('/console/_authenticated/_layout/campaigns/$publicId/_layout/ab-testing/_layout')({
    component: ABTestingLayout,
    loader: ({ params: { publicId } }) => fetchCampaign(publicId),
    validateSearch: zodValidator(abSearchSchema),
    errorComponent: () => (<div className="flex flex-col items-center justify-center h-full">
            <p className="text-lg font-semibold">{i18next.t('ab_testing.not_found', { ns: 'campaign' })}</p>
        </div>),
});
function ABTestingLayout() {
    const { publicId } = Route.useParams();
    const campaign = Route.useLoaderData();
    const { pathname } = useLocation();
    const { from, to, excludeDevices, excludeBrowsers } = Route.useSearch();
    const navigate = Route.useNavigate();
    const { t } = useTranslation('campaign');
    const [fromDate, toDate] = React.useMemo(() => [new Date(from), new Date(to)], [from, to]);
    const navTabs = React.useMemo(() => [
        {
            label: t('ab_testing.tabs.overview'),
            path: '/console/campaigns/$publicId/ab-testing',
            params: { publicId },
            pathname: `/console/campaigns/${publicId}/ab-testing`,
            search: { from, to, excludeDevices, excludeBrowsers },
        },
        {
            label: t('ab_testing.tabs.destinations'),
            path: '/console/campaigns/$publicId/ab-testing/destinations',
            pathname: `/console/campaigns/${publicId}/ab-testing/destinations`,
            params: { publicId },
            search: { from, to, excludeDevices, excludeBrowsers },
        },
        {
            label: t('ab_testing.tabs.smart_links'),
            path: '/console/campaigns/$publicId/ab-testing/smart-links',
            pathname: `/console/campaigns/${publicId}/ab-testing/smart-links`,
            params: { publicId },
            search: { from, to, excludeDevices, excludeBrowsers },
        },
        {
            label: t('ab_testing.tabs.smart_links_by_destination'),
            path: '/console/campaigns/$publicId/ab-testing/smart-links-by-destination',
            pathname: `/console/campaigns/${publicId}/ab-testing/smart-links-by-destination`,
            params: { publicId },
            search: { from, to, excludeDevices, excludeBrowsers },
        },
        {
            label: t('ab_testing.tabs.themes', { defaultValue: 'Theme' }),
            path: '/console/campaigns/$publicId/ab-testing/themes',
            pathname: `/console/campaigns/${publicId}/ab-testing/themes`,
            params: { publicId },
            search: { from, to, excludeDevices, excludeBrowsers },
        },
    ], [from, publicId, to, excludeDevices, excludeBrowsers, t]);
    const currentTab = navTabs.find((tab) => pathname.includes(tab.pathname));
    const handleRangeChange = React.useCallback((newFrom, newTo) => {
        navigate({
            search: {
                from: format(startOfDay(newFrom), DATE_FORMAT_ISO),
                to: format(endOfDay(newTo), DATE_FORMAT_ISO),
                excludeDevices,
                excludeBrowsers,
            },
        });
    }, [navigate, excludeDevices, excludeBrowsers]);
    const handleDevicesChange = React.useCallback((values) => {
        navigate({ search: { from, to, excludeDevices: values, excludeBrowsers } });
    }, [navigate, from, to, excludeBrowsers]);
    const handleBrowsersChange = React.useCallback((values) => {
        navigate({ search: { from, to, excludeDevices, excludeBrowsers: values } });
    }, [navigate, from, to, excludeDevices]);
    const periodLabel = `${format(fromDate, 'MMM d')} – ${format(toDate, 'MMM d, yyyy')}`;
    return (<div className="flex flex-col gap-6 p-6">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="space-y-1">
                    <h1 className="text-2xl font-semibold tracking-tight">
                        {t('ab_testing.title', { label: currentTab?.label ?? '' })}
                    </h1>
                    <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className="font-mono text-xs">
                            ad-{publicId}
                        </Badge>
                        <p className="text-sm text-muted-foreground">
                            {t('ab_testing.campaign_label', { name: campaign.name })}
                        </p>
                    </div>
                </div>
                <span className="rounded-full border border-border/60 bg-muted/40 px-3 py-1 text-xs text-muted-foreground tabular-nums">
                    {periodLabel}
                </span>
            </div>

            {/* Distinct layoutId so framer-motion's sliding indicator on the
            parent campaign nav (Information / Analytics / A/B Testing /
            Customize) doesn't share its motion target with this nested
            strip. Without this the two indicators would jump between
            each other when the parent re-renders. */}
            <div className="overflow-x-auto">
                <SegmentedTabs navItems={navTabs} layoutId="ab-testing-tabs"/>
            </div>

            <div className="sticky top-0 z-10 -mx-6 -my-1 rounded-xl border border-border/60 bg-card/70 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-card/50">
                <div className="flex flex-wrap items-end justify-between gap-3">
                    <FiltersRow from={fromDate} to={toDate} setRange={handleRangeChange}/>
                    <AnalyticsFilter excludeDevices={excludeDevices} excludeBrowsers={excludeBrowsers} onDevicesChange={handleDevicesChange} onBrowsersChange={handleBrowsersChange}/>
                </div>
            </div>

            <Outlet />
        </div>);
}
